import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
    ArrowLeftIcon, 
    MusicalNoteIcon, 
    MapPinIcon, 
    CalendarIcon, 
    PlusIcon,
    XMarkIcon,
    CurrencyDollarIcon,
    PhotoIcon
  } from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/ApiService';
import { useConnectionAwareSubmit } from '../../hooks/useConnectionAwareSubmit';
import { awardScenePoints } from '../../services/UserService';
import CreatableSelect from 'react-select/creatable';
import Select from 'react-select';
import { US_CITIES, US_STATES } from '../../utils/usCitiesStates.js';

const Container = styled.div`
  min-height: 100vh;
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text.primary};
`;

const Header = styled.header`
  background: ${props => props.theme.colors.surface};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  padding: ${props => props.theme.spacing.md} 0;
  position: sticky;
  top: 0;
  z-index: ${props => props.theme.zIndex.sticky};
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.lg};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
`;

const BackButton = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  background: none;
  border: none;
  color: ${props => props.theme.colors.text.secondary};
  cursor: pointer;
  padding: ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.md};
  
  &:hover {
    background: ${props => props.theme.colors.hover};
    color: ${props => props.theme.colors.text.primary};
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const Title = styled.h1`
  font-size: ${props => props.theme.fontSizes.xl};
  font-weight: 600;
  margin: 0;
`;

const FormContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: ${props => props.theme.spacing.xl};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
`;

const FormSection = styled.div`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.lg};
`;

const SectionTitle = styled.h2`
  font-size: ${props => props.theme.fontSizes.lg};
  font-weight: 600;
  margin: 0 0 ${props => props.theme.spacing.md} 0;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  
  svg {
    width: 20px;
    height: 20px;
    color: ${props => props.theme.colors.primary};
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.md};
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Label = styled.label`
  font-weight: 500;
  color: ${props => props.theme.colors.text.primary};
  font-size: ${props => props.theme.fontSizes.sm};
`;

const Input = styled.input`
  padding: ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background: ${props => props.theme.colors.input.background};
  color: ${props => props.theme.colors.text.primary};
  font-size: ${props => props.theme.fontSizes.md};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}20;
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.text.secondary};
  }
`;

const StyledSelect = styled.select`
  padding: ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background: ${props => props.theme.colors.input.background};
  color: ${props => props.theme.colors.text.primary};
  font-size: ${props => props.theme.fontSizes.md};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}20;
  }
`;

const TextArea = styled.textarea`
  padding: ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background: ${props => props.theme.colors.input.background};
  color: ${props => props.theme.colors.text.primary};
  font-size: ${props => props.theme.fontSizes.md};
  min-height: 120px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}20;
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.text.secondary};
  }
`;

const BandsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
`;

const BandInput = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  align-items: center;
`;

const AddBandButton = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  background: ${props => props.theme.colors.primary}20;
  border: 1px solid ${props => props.theme.colors.primary};
  border-radius: ${props => props.theme.borderRadius.md};
  color: ${props => props.theme.colors.primary};
  cursor: pointer;
  font-size: ${props => props.theme.fontSizes.sm};
  
  &:hover {
    background: ${props => props.theme.colors.primary}30;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const RemoveBandButton = styled.button`
  background: ${props => props.theme.colors.error}20;
  border: 1px solid ${props => props.theme.colors.error};
  border-radius: ${props => props.theme.borderRadius.md};
  color: ${props => props.theme.colors.error};
  cursor: pointer;
  padding: ${props => props.theme.spacing.sm};
  
  &:hover {
    background: ${props => props.theme.colors.error}30;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  justify-content: flex-end;
  margin-top: ${props => props.theme.spacing.xl};
`;

const Button = styled(motion.button)`
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.md};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SubmitButton = styled(Button)`
  background: ${props => props.theme.colors.gradient};
  border: none;
  color: white;
  
  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.gradientHover};
  }
`;

const CancelButton = styled(Button)`
  background: transparent;
  border: 1px solid ${props => props.theme.colors.border};
  color: ${props => props.theme.colors.text.secondary};
  
  &:hover {
    background: ${props => props.theme.colors.hover};
    color: ${props => props.theme.colors.text.primary};
  }
`;

const ErrorMessage = styled.span`
  color: ${props => props.theme.colors.error};
  font-size: ${props => props.theme.fontSizes.sm};
`;

const DeleteButton = styled(Button)`
  background: ${props => props.theme.colors.error};
  border: none;
  color: white;
  
  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.error}dd;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xl};
  max-width: 400px;
  width: 90%;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
`;

const ModalTitle = styled.h2`
  color: ${props => props.theme.colors.error};
  margin: 0 0 ${props => props.theme.spacing.md} 0;
  font-size: ${props => props.theme.fontSizes.lg};
`;

const ModalText = styled.p`
  color: ${props => props.theme.colors.text.secondary};
  margin: 0 0 ${props => props.theme.spacing.lg} 0;
  line-height: 1.5;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  justify-content: flex-end;
`;

const Genre = styled.span`
  display: inline-flex;
  align-items: center;
  background: ${props => props.theme.colors.accent};
  color: white;
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: ${props => props.theme.typography.small.fontSize};
  font-weight: 500;
  margin-right: ${props => props.theme.spacing.xs};
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const genres = [
  // Rock & Alternative
  'Rock', 'Indie Rock', 'Alternative Rock', 'Classic Rock', 'Progressive Rock', 'Post-Rock', 'Shoegaze',
  
  // Heavy Music
  'Metal', 'Death Metal', 'Black Metal', 'Doom Metal', 'Thrash Metal', 'Heavy Metal', 'Hardcore', 
  'Grindcore', 'Metalcore', 'Deathcore', 'Post-Hardcore',
  
  // Punk
  'Punk', 'Pop Punk', 'Hardcore Punk', 'Post-Punk', 'Punk Rock',
  
  // Electronic
  'Electronic', 'House', 'Techno', 'Dubstep', 'Drum & Bass', 'Ambient', 'IDM', 'Trance', 'EDM', 
  'Synth-pop', 'Synthwave', 'Experimental Electronic',
  
  // Hip Hop & Rap
  'Hip Hop', 'Rap', 'Trap', 'Underground Hip Hop', 'Conscious Rap',
  
  // Folk & Acoustic
  'Folk', 'Acoustic', 'Singer-Songwriter', 'Indie Folk', 'Folk Rock', 'Americana',
  
  // Country
  'Country', 'Alt-Country', 'Bluegrass', 'Country Rock', 'Outlaw Country',
  
  // Jazz & Blues
  'Jazz', 'Blues', 'Blues Rock', 'Jazz Fusion', 'Swing', 'Bebop',
  
  // Soul & Funk
  'R&B', 'Soul', 'Funk', 'Neo-Soul', 'Motown',
  
  // Reggae & Ska
  'Reggae', 'Ska', 'Ska Punk', 'Dub', 'Dancehall',
  
  // Pop
  'Pop', 'Indie Pop', 'Dream Pop', 'Electropop', 'Art Pop',
  
  // Other
  'Classical', 'Experimental', 'Noise', 'Avant-garde', 'Other'
];

const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: '#fff',
    color: '#222',
    borderColor: state.isFocused ? '#8B5CF6' : '#ccc',
    boxShadow: state.isFocused ? '0 0 0 2px #8B5CF633' : provided.boxShadow,
    minHeight: 40,
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: '#fff',
    color: '#222',
    zIndex: 9999,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? '#8B5CF622' : '#fff',
    color: '#222',
    cursor: 'pointer',
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#222',
  }),
  input: (provided) => ({
    ...provided,
    color: '#222',
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#888',
  }),
};

const AddShow = () => {
  const navigate = useNavigate();
  const { showId } = useParams();
  const { user: currentUser, loading: authLoading } = useAuth();
  const { submitWithConnectionCheck, isSubmitting, isWaitingForConnection } = useConnectionAwareSubmit();
  const [loading, setLoading] = useState(!!showId); // Loading state for edit mode
  const [bands, setBands] = useState(['']);
  const [selectedGenres, setSelectedGenres] = useState(['Rock']);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  
  const isEditMode = !!showId;
  
  const { register, handleSubmit, formState: { errors }, setValue } = useForm({
    defaultValues: {
      city: 'Gainesville',
      state: 'FL',
      ticketPrice: 0,
      ageRestriction: 'all-ages'
    }
  });

  // Redirect to login if not authenticated (but wait for auth to finish loading)
  useEffect(() => {
    console.log('🔍 AddShow: Auth state check - authLoading:', authLoading, 'currentUser:', currentUser?.email || 'null');
    if (!authLoading && !currentUser) {
      console.log('🔍 AddShow: Redirecting to login - auth finished loading but no user found');
      navigate('/login');
    }
  }, [currentUser, authLoading, navigate]);

  // Load existing show data for editing
  useEffect(() => {
    const loadShowData = async () => {
      if (!showId) return;
      
      try {
        const showData = await apiService.getShowById(showId);
        
        // Prepopulate form fields
        setValue('venue', showData.venue_name || '');
        setValue('address', showData.venue_address || '');
        setValue('city', showData.city || 'Gainesville');
        setValue('state', showData.state || 'FL');
        setValue('date', showData.show_date || '');
        setValue('time', showData.show_time || '');
        setValue('ticketPrice', showData.ticket_price || 0);
        setValue('ageRestriction', showData.age_limit || 'all-ages');
        setValue('description', showData.description || '');
        
        // Handle bands (split by " & " separator)
        if (showData.band_name) {
          const bandArray = showData.band_name.split(' & ').map(band => band.trim()).filter(band => band);
          setBands(bandArray.length > 0 ? bandArray : ['']);
        }
        
        // Handle genres
        if (showData.genre) {
          // If genre is a string, treat it as a single genre
          if (typeof showData.genre === 'string') {
            // Check if it's already an enum value or display name
            const enumValues = ['rock', 'punk', 'metal', 'indie', 'alternative', 'hardcore', 'folk', 'country', 'blues', 'jazz', 'electronic', 'hip_hop', 'reggae', 'ska', 'grunge', 'psychedelic', 'experimental', 'other'];
            
            if (enumValues.includes(showData.genre)) {
              // It's an enum value, convert to display name
              const reverseMapping = {
                'rock': 'Rock',
                'indie': 'Indie Rock',
                'alternative': 'Alternative Rock',
                'metal': 'Metal',
                'hardcore': 'Hardcore',
                'punk': 'Punk',
                'electronic': 'Electronic',
                'hip_hop': 'Hip Hop',
                'folk': 'Folk',
                'country': 'Country',
                'blues': 'Blues',
                'jazz': 'Jazz',
                'reggae': 'Reggae',
                'ska': 'Ska',
                'grunge': 'Grunge',
                'psychedelic': 'Psychedelic',
                'experimental': 'Experimental',
                'other': 'Other'
              };
              setSelectedGenres([reverseMapping[showData.genre] || 'Rock']);
            } else {
              // It's already a display name
              setSelectedGenres([showData.genre]);
            }
          } else if (Array.isArray(showData.genre)) {
            setSelectedGenres(showData.genre);
          } else {
            setSelectedGenres(['Rock']);
          }
        }
        
        // Extract additional genres from description if present
        if (showData.description) {
          const genreMatch = showData.description.match(/\[GENRES:(.*?)\]/);
          if (genreMatch) {
            const additionalGenres = genreMatch[1].split(',').map(g => g.trim()).filter(g => g);
            if (additionalGenres.length > 0) {
              setSelectedGenres(additionalGenres);
            }
            // Clean up description by removing the genres tag
            const cleanDescription = showData.description.replace(/\n\n\[GENRES:.*?\]/, '').trim();
            setValue('description', cleanDescription);
          }
        }
        
      } catch (error) {
        console.error('Error loading show data:', error);
        toast.error('Failed to load show data');
        navigate('/my-shows');
      } finally {
        setLoading(false);
      }
    };

    loadShowData();
  }, [showId, setValue, navigate]);

  useEffect(() => {
    const lastLocation = JSON.parse(localStorage.getItem('lastLocation'));
    if (lastLocation) {
      let cityOption = US_CITIES.find(opt => opt.value.toLowerCase() === (lastLocation.city || '').toLowerCase());
      if (!cityOption && lastLocation.city) {
        cityOption = { value: lastLocation.city, label: lastLocation.city };
      }
      setSelectedCity(cityOption || null);
      setValue('city', lastLocation.city || '');
      const stateOption = US_STATES.find(opt => opt.value === (lastLocation.state || ''));
      setSelectedState(stateOption || null);
      setValue('state', lastLocation.state || '');
    }
  }, [setValue]);

  const addBand = () => {
    setBands([...bands, '']);
  };

  const removeBand = (index) => {
    setBands(bands.filter((_, i) => i !== index));
  };

  const updateBand = (index, value) => {
    const newBands = [...bands];
    newBands[index] = value;
    setBands(newBands);
  };

  const addGenre = (genre) => {
    if (selectedGenres.length < 3 && !selectedGenres.includes(genre)) {
      setSelectedGenres([...selectedGenres, genre]);
    }
  };

  const removeGenre = (genre) => {
    setSelectedGenres(selectedGenres.filter(g => g !== genre));
  };

  // Genre mapping from display names to enum values
  const mapGenreToEnum = (displayGenre) => {
    const genreMapping = {
      'Rock': 'rock',
      'Indie Rock': 'indie',
      'Alternative Rock': 'alternative',
      'Classic Rock': 'rock',
      'Progressive Rock': 'rock',
      'Post-Rock': 'rock',
      'Shoegaze': 'rock',
      'Metal': 'metal',
      'Death Metal': 'metal',
      'Black Metal': 'metal',
      'Doom Metal': 'metal',
      'Thrash Metal': 'metal',
      'Heavy Metal': 'metal',
      'Hardcore': 'hardcore',
      'Grindcore': 'hardcore',
      'Metalcore': 'metal',
      'Deathcore': 'metal',
      'Post-Hardcore': 'hardcore',
      'Punk': 'punk',
      'Pop Punk': 'punk',
      'Hardcore Punk': 'punk',
      'Post-Punk': 'punk',
      'Punk Rock': 'punk',
      'Electronic': 'electronic',
      'House': 'electronic',
      'Techno': 'electronic',
      'Dubstep': 'electronic',
      'Drum & Bass': 'electronic',
      'Ambient': 'electronic',
      'IDM': 'electronic',
      'Trance': 'electronic',
      'EDM': 'electronic',
      'Synth-pop': 'electronic',
      'Synthwave': 'electronic',
      'Experimental Electronic': 'electronic',
      'Hip Hop': 'hip_hop',
      'Rap': 'hip_hop',
      'Trap': 'hip_hop',
      'Underground Hip Hop': 'hip_hop',
      'Conscious Rap': 'hip_hop',
      'Folk': 'folk',
      'Acoustic': 'folk',
      'Singer-Songwriter': 'folk',
      'Indie Folk': 'folk',
      'Folk Rock': 'folk',
      'Americana': 'folk',
      'Country': 'country',
      'Alt-Country': 'country',
      'Bluegrass': 'country',
      'Country Rock': 'country',
      'Outlaw Country': 'country',
      'Jazz': 'jazz',
      'Blues': 'blues',
      'Blues Rock': 'blues',
      'Jazz Fusion': 'jazz',
      'Swing': 'jazz',
      'Bebop': 'jazz',
      'R&B': 'hip_hop',
      'Soul': 'hip_hop',
      'Funk': 'hip_hop',
      'Neo-Soul': 'hip_hop',
      'Motown': 'hip_hop',
      'Reggae': 'reggae',
      'Ska': 'ska',
      'Ska Punk': 'ska',
      'Dub': 'reggae',
      'Dancehall': 'reggae',
      'Pop': 'other',
      'Indie Pop': 'indie',
      'Dream Pop': 'indie',
      'Electropop': 'electronic',
      'Art Pop': 'other',
      'Classical': 'other',
      'Experimental': 'experimental',
      'Noise': 'experimental',
      'Avant-garde': 'experimental',
      'Other': 'other'
    };
    
    return genreMapping[displayGenre] || 'other';
  };

  const onSubmit = async (data) => {
    // Validate form data first
    const validBands = bands.map(band => band.trim()).filter(band => band.length >= 1);
    
    if (validBands.length === 0) {
      toast.error('Please add at least one band (min 1 character each)');
      return;
    }

    if (selectedGenres.length === 0) {
      toast.error('Please select at least one genre');
      return;
    }

    // Create the submission function
    const submitFunction = async () => {
      if (isEditMode) {
        // Update existing show
        const updateData = {
          title: validBands.join(' & '), // Auto-generate title from band names
          band_name: validBands.join(' & '), // Use band_name instead of bands array
          venue_name: data.venue,
          venue_address: data.address,
          city: selectedCity ? selectedCity.value : data.city.trim(),
          state: selectedState ? selectedState.value : data.state.trim().toUpperCase(),
          show_date: data.date,
          show_time: data.time,
          ticket_price: data.ticketPrice.toString(),
          age_limit: data.ageRestriction,
          genre: mapGenreToEnum(selectedGenres[0]), // Primary genre only
          description: `${data.description || ''}\n\n[GENRES:${selectedGenres.join(',')}]`
        };

        const result = await apiService.updateShow(showId, updateData);
        return { success: true, data: result };
      } else {
        // Create new show - only use correct database field names
        const showDataWithGenres = {
          title: validBands.join(' & '),
          band_name: validBands.join(' & '),
          venue_name: data.venue,
          venue_address: data.address,
          city: selectedCity ? selectedCity.value : data.city.trim(),
          state: selectedState ? selectedState.value : data.state.trim().toUpperCase(),
          show_date: data.date,
          show_time: data.time,
          ticket_price: data.ticketPrice?.toString() || '0',
          age_limit: data.ageRestriction,
          genre: mapGenreToEnum(selectedGenres[0]),
          description: `${data.description || ''}\n\n[GENRES:${selectedGenres.join(',')}]`,
          poster_id: currentUser.id
        };
        
        return await apiService.createShow(showDataWithGenres);
      }
    };

    // Submit with connection awareness
    const result = await submitWithConnectionCheck(submitFunction, {
      loadingMessage: isEditMode ? 'Updating show...' : 'Adding show...',
      successMessage: isEditMode ? 'Show updated successfully!' : 'Show added successfully!',
      errorMessage: isEditMode ? 'Failed to update show' : 'Failed to add show',
      offlineMessage: 'No internet connection. Your show data is saved and will be submitted when connection is restored...',
      retryMessage: 'Connection restored! Submitting your show...'
    });

    if (result.success && !isEditMode) {
      // Award scene points for new show
      let points = 5;
      if (currentUser.type === 'band' || currentUser.type === 'venue') {
        points = 10;
      }
      try {
        await awardScenePoints(currentUser.id, points, 'Posted a show', result.data?.id);
      } catch (err) {
        toast.error('Show posted, but failed to award scene points.');
      }
    }

    if (result.success) {
      // Navigate on success
      navigate(isEditMode ? '/my-shows' : '/', { replace: true });
    }
  };

  const handleBack = () => {
    // Follow the navigation hierarchy
    if (isEditMode) {
      // Edit Show → My Shows (where edit button was clicked)
      navigate('/my-shows', { replace: true });
    } else {
      // Add Show → Home (top level)
      navigate('/', { replace: true });
    }
  };

  const handleCancel = () => {
    // Follow the navigation hierarchy
    if (isEditMode) {
      // Edit Show → My Shows
      navigate('/my-shows', { replace: true });
    } else {
      // Add Show → Home
      navigate('/', { replace: true });
    }
  };

  const handleDelete = async () => {
    const deleteFunction = async () => {
      const result = await apiService.deleteShow(showId);
      return { success: true, data: result };
    };

    const result = await submitWithConnectionCheck(deleteFunction, {
      loadingMessage: 'Deleting show...',
      successMessage: 'Show deleted successfully!',
      errorMessage: 'Failed to delete show',
      offlineMessage: 'No internet connection. The show will be deleted when connection is restored...',
      retryMessage: 'Connection restored! Deleting show...'
    });

    setShowDeleteConfirm(false);
    
    if (result.success) {
      navigate('/my-shows', { replace: true });
    }
  };

  if (authLoading || !currentUser) {
    console.log('🔍 AddShow: Showing loading screen - authLoading:', authLoading, 'currentUser:', currentUser?.email || 'null');
    return (
      <Container>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '50vh',
          flexDirection: 'column',
          gap: '1rem' 
        }}>
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          >
            <MusicalNoteIcon style={{ width: 48, height: 48, color: '#8B5CF6' }} />
          </motion.div>
          <span style={{ color: '#b3b3b3' }}>Loading...</span>
        </div>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '50vh',
          flexDirection: 'column',
          gap: '1rem' 
        }}>
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          >
            <MusicalNoteIcon style={{ width: 48, height: 48, color: '#8B5CF6' }} />
          </motion.div>
          <span style={{ color: '#b3b3b3' }}>Loading show data...</span>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <HeaderContent>
          <BackButton
            onClick={handleBack}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <ArrowLeftIcon />
            Back
          </BackButton>
          <Title>{isEditMode ? 'Edit Show' : 'Add New Show'}</Title>
        </HeaderContent>
      </Header>

      <FormContainer>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <FormSection>
            <SectionTitle>
              <MusicalNoteIcon />
              Show Details
            </SectionTitle>
            
            <FormGroup>
              <Label>Bands/Artists</Label>
              <BandsList>
                {bands.map((band, index) => (
                  <BandInput key={index}>
                    <Input
                      value={band}
                      onChange={(e) => updateBand(index, e.target.value)}
                      placeholder={`Band ${index + 1}`}
                    />
                    {bands.length > 1 && (
                      <RemoveBandButton
                        type="button"
                        onClick={() => removeBand(index)}
                      >
                        <XMarkIcon />
                      </RemoveBandButton>
                    )}
                  </BandInput>
                ))}
              </BandsList>
              <AddBandButton
                type="button"
                onClick={addBand}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <PlusIcon />
                Add Band
              </AddBandButton>
            </FormGroup>

            <FormGroup>
              <Label>Genres (Select up to 3)</Label>
              <div style={{ marginBottom: '8px' }}>
                {selectedGenres.map((genre) => (
                  <Genre key={genre}>
                    {genre}
                    <button
                      type="button"
                      onClick={() => removeGenre(genre)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'white',
                        marginLeft: '8px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      ×
                    </button>
                  </Genre>
                ))}
              </div>
              <select
                value=""
                onChange={(e) => {
                  if (e.target.value) {
                    addGenre(e.target.value);
                    e.target.value = '';
                  }
                }}
                disabled={selectedGenres.length >= 3}
                style={{ padding: 8, borderRadius: 6, border: '1px solid #ccc', minWidth: 180 }}
              >
                <option value="">
                  {selectedGenres.length >= 3 ? 'Maximum 3 genres selected' : 'Add a genre...'}
                </option>
                {genres.filter(genre => !selectedGenres.includes(genre)).map(genre => (
                  <option key={genre} value={genre}>{genre}</option>
                ))}
              </select>
              {selectedGenres.length === 0 && <ErrorMessage>Please select at least one genre</ErrorMessage>}
            </FormGroup>

            <FormGroup>
              <Label>Description</Label>
              <TextArea
                {...register('description')}
                placeholder="Tell people about this show..."
              />
            </FormGroup>
          </FormSection>

          <FormSection>
            <SectionTitle>
              <CalendarIcon />
              Date & Time
            </SectionTitle>
            
            <FormRow>
              <FormGroup>
                <Label>Date</Label>
                <Input
                  type="date"
                  {...register('date', { required: 'Date is required' })}
                />
                {errors.date && <ErrorMessage>{errors.date.message}</ErrorMessage>}
              </FormGroup>
              
              <FormGroup>
                <Label>Time</Label>
                <Input
                  type="time"
                  {...register('time', { required: 'Time is required' })}
                />
                {errors.time && <ErrorMessage>{errors.time.message}</ErrorMessage>}
              </FormGroup>
            </FormRow>
          </FormSection>

          <FormSection>
            <SectionTitle>
              <MapPinIcon />
              Location
            </SectionTitle>
            
            <FormGroup>
              <Label htmlFor="venue">Venue</Label>
              <Input
                id="venue"
                {...register('venue', { required: 'Venue is required' })}
                placeholder="Enter venue name"
              />
              {errors.venue && <ErrorMessage>{errors.venue.message}</ErrorMessage>}
            </FormGroup>

            <FormRow>
              <FormGroup>
                <Label>City</Label>
                <CreatableSelect
                  options={US_CITIES}
                  value={selectedCity}
                  onChange={option => {
                    setSelectedCity(option);
                    setValue('city', option ? option.value : '');
                  }}
                  placeholder="Select or type your city..."
                  isClearable
                  styles={customSelectStyles}
                />
                {errors.city && <ErrorMessage>{errors.city.message}</ErrorMessage>}
              </FormGroup>
              
              <FormGroup>
                <Label>State</Label>
                <Select
                  options={US_STATES}
                  value={selectedState}
                  onChange={option => {
                    setSelectedState(option);
                    setValue('state', option ? option.value : '');
                  }}
                  placeholder="Select your state..."
                  isClearable
                  styles={customSelectStyles}
                />
                {errors.state && <ErrorMessage>{errors.state.message}</ErrorMessage>}
              </FormGroup>
            </FormRow>

            <FormGroup>
              <Label>Address</Label>
              <Input
                {...register('address')}
                placeholder="Enter venue address"
              />
            </FormGroup>
          </FormSection>

          <FormSection>
            <SectionTitle>
              <CurrencyDollarIcon />
              Ticket Info
            </SectionTitle>
            
            <FormRow>
              <FormGroup>
                <Label>Ticket Price ($)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  {...register('ticketPrice')}
                  placeholder="0.00"
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Age Restriction</Label>
                <select {...register('ageRestriction')} style={{ padding: 8, borderRadius: 6, border: '1px solid #ccc', minWidth: 120 }}>
                  <option value="all-ages">All Ages</option>
                  <option value="18+">18+</option>
                  <option value="21+">21+</option>
                </select>
              </FormGroup>
            </FormRow>
          </FormSection>

          <FormSection>
            <SectionTitle>
              <PhotoIcon />
              Show Images
            </SectionTitle>
            
            <div style={{
              padding: '40px 20px',
              textAlign: 'center',
              background: '#f9fafb',
              borderRadius: '12px',
              border: '2px dashed #4B5563',
              color: '#9CA3AF'
            }}>
              <PhotoIcon style={{ width: 48, height: 48, margin: '0 auto 16px', opacity: 0.5 }} />
              <h3 style={{ margin: '0 0 8px 0', color: '#6B7280' }}>Image Upload Coming Soon!</h3>
              <p style={{ margin: 0, fontSize: '14px' }}>We're working on adding image support for show posts.</p>
            </div>
          </FormSection>

          <ButtonGroup>
            <CancelButton
              type="button"
              onClick={handleCancel}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Cancel
            </CancelButton>
            
            {isEditMode && (
              <DeleteButton
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                ✕ Delete Show
              </DeleteButton>
            )}
            
            <SubmitButton
              type="submit"
              disabled={isSubmitting || isWaitingForConnection}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {isWaitingForConnection ? 'Waiting for connection...' :
               isSubmitting 
                ? (isEditMode ? 'Updating Show...' : 'Adding Show...') 
                : (isEditMode ? 'Update Show' : 'Add Show')
              }
            </SubmitButton>
          </ButtonGroup>
        </Form>
      </FormContainer>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <ModalOverlay>
          <ModalContent>
            <ModalTitle>Delete Show</ModalTitle>
            <ModalText>
              Are you sure you want to delete this show? This action cannot be undone and will permanently remove the show from SceneFeed.
            </ModalText>
            <ModalButtons>
              <CancelButton
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Cancel
              </CancelButton>
              <DeleteButton
                type="button"
                onClick={handleDelete}
                disabled={isSubmitting || isWaitingForConnection}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {isWaitingForConnection ? 'Waiting for connection...' :
                 isSubmitting ? 'Deleting...' : 'Delete Show'}
              </DeleteButton>
            </ModalButtons>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default AddShow; 